import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ClipboardDocumentListIcon, 
  ClockIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { formatNumber, formatDistanceToNow, getPriorityColor, getStatusColor, formatDuration } from '../utils';
import AttendanceWidget from './AttendanceWidget';

const MechanicDashboard = ({ data }) => {
  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-dark-300">No dashboard data available</p>
      </div>
    );
  }

  const { overview, charts, upcoming, recent } = data;

  const stats = [
    {
      name: 'Active Tasks',
      value: formatNumber(overview.activeTasks),
      icon: ClockIcon,
      color: 'bg-blue-600',
      href: '/tasks?status=in-progress',
    },
    {
      name: 'Completed Tasks',
      value: formatNumber(overview.completedTasks),
      icon: CheckCircleIcon,
      color: 'bg-green-600',
      href: '/tasks?status=completed',
    },
    {
      name: 'Overdue Tasks',
      value: formatNumber(overview.overdueTasks),
      icon: ExclamationTriangleIcon,
      color: 'bg-red-600',
      href: '/tasks?status=overdue',
    },
    {
      name: 'Today\'s Tasks',
      value: formatNumber(overview.todayTasks),
      icon: CalendarDaysIcon,
      color: 'bg-purple-600',
      href: '/tasks?date=today',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="relative bg-dark-800 border border-dark-700 rounded-lg p-6 hover:bg-dark-700 transition-colors"
          >
            <div className="flex items-center">
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-dark-300">{stat.name}</p>
                <p className="text-2xl font-bold text-white">{stat.value}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Attendance Widget */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <AttendanceWidget />
        </div>
        <div className="lg:col-span-2">
          {/* Weekly Performance */}
          <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
            <h3 className="text-lg font-medium text-white mb-4">This Week's Performance</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-primary-500">{overview.completedThisWeek || 0}</p>
                <p className="text-sm text-dark-300">Tasks Completed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-primary-500">{overview.completedThisMonth || 0}</p>
                <p className="text-sm text-dark-300">This Month</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-primary-500">
                  {formatDuration(overview.totalTimeThisMonth || 0)}
                </p>
                <p className="text-sm text-dark-300">Total Time</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Task Priority Chart */}
      <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
        <h3 className="text-lg font-medium text-white mb-4">Your Tasks by Priority</h3>
        <div className="space-y-3">
          {charts.tasksByPriority.map((item) => (
            <div key={item._id} className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full ${getPriorityColor(item._id).split(' ')[1]} mr-3`}></div>
                <span className="text-sm text-dark-300 capitalize">{item._id}</span>
              </div>
              <span className="text-sm font-medium text-white">{item.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming Tasks and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Tasks */}
        {upcoming?.upcomingTasks && (
          <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Upcoming Tasks</h3>
              <Link to="/tasks" className="text-sm text-primary-500 hover:text-primary-400">
                View all
              </Link>
            </div>
            <div className="space-y-4">
              {upcoming.upcomingTasks.slice(0, 5).map((task) => (
                <div key={task._id} className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white truncate">{task.title}</p>
                    <p className="text-xs text-dark-400">
                      {task.car && `${task.car.numberPlate} • `}
                      {task.dueDate && `Due ${formatDistanceToNow(task.dueDate)}`}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </span>
                    <button className="p-1 text-primary-500 hover:text-primary-400">
                      <PlayIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Completed Tasks */}
        {recent?.recentCompletedTasks && (
          <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Recently Completed</h3>
              <Link to="/tasks?status=completed" className="text-sm text-primary-500 hover:text-primary-400">
                View all
              </Link>
            </div>
            <div className="space-y-4">
              {recent.recentCompletedTasks.slice(0, 5).map((task) => (
                <div key={task._id} className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white truncate">{task.title}</p>
                    <p className="text-xs text-dark-400">
                      {task.car && `${task.car.numberPlate} • `}
                      Completed {formatDistanceToNow(task.completedAt)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </span>
                    <CheckCircleIcon className="h-4 w-4 text-green-500" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
        <h3 className="text-lg font-medium text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/tasks?status=pending"
            className="flex items-center justify-center p-4 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors"
          >
            <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
            Start Next Task
          </Link>
          <Link
            to="/tasks?status=in-progress"
            className="flex items-center justify-center p-4 bg-yellow-600 hover:bg-yellow-700 rounded-lg text-white transition-colors"
          >
            <ClockIcon className="h-5 w-5 mr-2" />
            Resume Task
          </Link>
          <Link
            to="/cars"
            className="flex items-center justify-center p-4 bg-green-600 hover:bg-green-700 rounded-lg text-white transition-colors"
          >
            <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
            View Cars
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MechanicDashboard;